import { IconChevronDownV2 } from '@blockchain-com/icons'
import Currency from 'components/Currency'
import I18n from 'components/I18n'
import Icon from 'components/Icons/Icon'
import { actions, selectors } from 'data'
import { ToAccountType, WithdrawalMethodType } from 'data/Analytics/types'
import { WhitelistAddressType } from 'data/types'
import messages from 'lang'
import React, { useEffect } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { connect, ConnectedProps } from 'react-redux'
import styled from 'styled-components'
import { color } from 'utils/styledHelper'

import { Error, Input, InputContainer, Label } from '../styles'
import MetadataInput from './MetadataInput'

const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  label {
    color: ${color('grey200')};
  }
`

const Link = styled.a`
  color: ${color('primary')};
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 150%;
  text-decoration: none;

  :hover {
    color: ${color('blue400')};
  }
`

const SelectWrapper = styled.div`
  position: relative;

  svg {
    pointer-events: none;
    position: absolute;
    right: 1rem;
    top: 1rem;
  }
`

const StyledSelect = styled.select`
  appearance: none;
  width: 100%;
  font-weight: 500;
  font-size: 1rem;
  line-height: 150%;
  color: ${color('dark')};
  padding: 1rem;
  cursor: pointer;
  border: 1px solid ${color('dark500')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  background-color: transparent;
`

type BeneficiaryOptions = Array<{
  address?: string
  name: string
  value: string
}>
type SelectProps = {
  onChange: (beneficiaryType: string) => void
  options: BeneficiaryOptions
  value: string
}

const Select = ({ onChange, options, value }: SelectProps) => {
  useEffect(() => {
    // trigger a change if value is not a valid option
    if (!options.find((o) => o.value === value)) {
      onChange(options[0].value)
    }
  }, [options.length])

  if (options.length === 1) {
    return null
  }

  return (
    <SelectWrapper>
      <StyledSelect
        data-e2e='withdrawalAddressDropdown'
        name='withdrawalAddressDropdown'
        onChange={(e) => onChange(e.target.value)}
        value={value}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.name}
          </option>
        ))}
      </StyledSelect>
      <Icon color='grey400' size='sm'>
        <IconChevronDownV2 />
      </Icon>
    </SelectWrapper>
  )
}

const getBeneficiaryOptions = (linkedWalletAddress, whitelistAddresses) => {
  let options: BeneficiaryOptions = []

  if (linkedWalletAddress) {
    options.push({
      address: linkedWalletAddress,
      name: 'My Blockchain.com Wallet',
      value: 'wallet'
    })
  }

  options = options.concat(
    whitelistAddresses.map((add) => ({
      name: add.text,
      value: add.value
    }))
  )

  options.push({ name: 'External Address', value: 'external' })

  return options
}

type Props = {
  currency: string
  error: React.ReactNode
  metadata: string
  onMetadataChange: (beneficiaryMetadata: string) => void
  onTypeChange: (beneficiaryType: string) => void
  onValueChange: (beneficiaryValue: string) => void
  type: string
  value: string
  whitelistAddresses: WhitelistAddressType[]
} & ConnectedProps<typeof connector> &
  WrappedComponentProps

const BeneficiarySelect = ({
  currency,
  currencyData,
  error,
  intl,
  linkedWalletAddress,
  memoTokens,
  metadata,
  onMetadataChange,
  onTypeChange,
  onValueChange,
  showWhitelistModal,
  trackMethodChange,
  type,
  value,
  whitelistAddresses
}: Props) => {
  const options = getBeneficiaryOptions(linkedWalletAddress, whitelistAddresses)
  const externalOrWalletDestination = type === 'external' || type === 'wallet'
  return (
    <>
      <InputContainer>
        <LabelWrapper>
          <Label>
            <I18n data={messages.label_withdrawal_address} value={<Currency code={currency} />} />
          </Label>
          {whitelistAddresses.length === 0 && (
            <Link href='#' onClick={showWhitelistModal}>
              <I18n data={messages.whitelisting_enable} />
            </Link>
          )}
        </LabelWrapper>
        <Select
          onChange={(newType) => {
            onTypeChange(newType)
            trackMethodChange(currency, newType === 'wallet' ? 'TRADING' : 'EXTERNAL')
            if (newType === 'wallet') onValueChange(options[0].address || '')
            else onValueChange('')
          }}
          options={options}
          value={type}
        />
        {type === 'external' && (
          <Input
            data-e2e='withdrawDestinationAddress'
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={intl.formatMessage(messages.label_withdrawal_address, {
              value: currencyData?.displaySymbol
            })}
            value={value}
          />
        )}
      </InputContainer>
      {externalOrWalletDestination && memoTokens.includes(currency) && (
        <MetadataInput
          e2e={currency === 'XRP' ? 'withdrawalTag' : 'withdrawalMemo'}
          label={
            <I18n
              data={currency === 'XRP' ? messages.label_tag : messages.label_memo}
              value={currency}
            />
          }
          name='tag'
          onChange={onMetadataChange}
          value={metadata}
        />
      )}
      <Error>{error}</Error>
    </>
  )
}

const mapStateToProps = (state, { currency }) => {
  const whitelistAddresses = selectors.payments
    .getWhitelistAddressesOptions(state)
    .filter((add) => add.currency === currency && add.state === 'ACTIVE')
  const isWalletLinked = selectors.auth.isWalletLinked(state)
  const currencyData = selectors.currencies.getCurrency(state, currency)
  return {
    currencyData,
    linkedWalletAddress: isWalletLinked
      ? selectors.withdrawal.getWalletAddress(state)(currency)
      : undefined,
    memoTokens: selectors.config.getConfig(state).memoTokens,
    whitelistAddresses
  }
}

const mapDispatchToProps = (dispatch) => ({
  showWhitelistModal: () => {
    dispatch(actions.app.showModal('ENABLE_WHITELIST_ADDRESS'))
  },
  trackMethodChange: (currency: string, target: string) => {
    dispatch(
      actions.analytics.trackEvent({
        key: 'Withdrawal Method Selected',
        properties: {
          currency,
          to_account_type: target as ToAccountType,
          withdrawal_method: 'CRYPTO' as WithdrawalMethodType
        }
      })
    )
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(injectIntl(BeneficiarySelect))
